import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PLACEHOLDER_EMAIL } from '../lib/contactDefaults';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Loader2, 
  Save, 
  Webhook,
  Info,
  ExternalLink,
  Cloud,
  Landmark,
  CheckCircle2,
  XCircle,
  FolderPlus,
  Link2,
  Unlink,
  Users,
  Plus,
  Edit,
  Trash2,
  Key,
  Mail,
  UserCog,
  CalendarDays,
  Clock,
  Check,
  X,
  Palmtree,
  Download,
  Upload,
  Database,
  Shield,
  Trash,
  RotateCcw,
  AlertTriangle,
  Hash
} from 'lucide-react';
import { Camera, Building2, Brain, ToggleLeft, Store, HardHat, GitMerge, Eye } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';

// Import extracted tab components
import { LeaveManagementTab } from '../components/settings/LeaveManagementTab';
import { RecycleBinTab } from '../components/settings/RecycleBinTab';
import { BackupTab } from '../components/settings/BackupTab';
import { CaptureSettingsTab } from '../components/settings/CaptureSettingsTab';
import { EntitiesSettingsTab } from '../components/settings/EntitiesSettingsTab';
import { InboxMemoryTab } from '../components/settings/InboxMemoryTab';
import { FeaturesTab } from '../components/settings/FeaturesTab';
import { StaffTeamPicker } from '../components/settings/StaffTeamPicker';
import { OrderSuppliersTab } from '../components/settings/OrderSuppliersTab';
import { BuildingControlTab } from '../components/settings/BuildingControlTab';
import { MergeProjectsTab } from '../components/settings/MergeProjectsTab';
import { ImpersonationLogTab } from '../components/settings/ImpersonationLogTab';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', description: 'Full system access' },
  { value: 'surveyor', label: 'Surveyor', description: 'Manage projects and schedules' },
  { value: 'operative', label: 'Operative', description: 'View projects and schedules' }
];

const USER_TYPE_OPTIONS = [
  { value: 'admin', label: 'Admin', description: 'Full system access' },
  { value: 'surveyor', label: 'Surveyor', description: 'Add projects/tenders/contacts, view only after creation' },
  { value: 'employee', label: 'Employee', description: 'Internal staff with holiday tracking' },
  { value: 'sub_contractor', label: 'Sub-contractor', description: 'External worker with trade & pay rate' }
];

const TRADE_OPTIONS = [
  'Carpenter', 'Electrician', 'Plumber', 'Labourer', 'Bricklayer', 
  'Plasterer', 'Painter', 'Roofer', 'Tiler', 'Groundworker', 'General'
];

const ROLE_COLORS = {
  'admin': 'bg-red-500/20 text-red-400 border-red-500/30',
  'surveyor': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'operative': 'bg-green-500/20 text-green-400 border-green-500/30'
};

const USER_TYPE_COLORS = {
  'admin': 'bg-red-500/20 text-red-400 border-red-500/30',
  'surveyor': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'employee': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'sub_contractor': 'bg-amber-500/20 text-amber-400 border-amber-500/30'
};

const VACATION_STATUS_COLORS = {
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  denied: 'bg-red-500/20 text-red-400 border-red-500/30'
};

export function Settings() {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [oneDriveStatus, setOneDriveStatus] = useState({ connected: false, loading: true });
  const [xeroStatus, setXeroStatus] = useState({ enabled: false, connected: false, configured: false, loading: true });
  const [settingUpFolders, setSettingUpFolders] = useState(false);
  const [activeTab, setActiveTab] = useState('integrations'); // integrations, users, staff, leave, templates, backup, recycle
  
  // User management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userForm, setUserForm] = useState({ 
    name: '', 
    email: PLACEHOLDER_EMAIL, 
    user_type: 'employee',
    trade: '',
    pay_rate: '',
    is_app_user: false,
    annual_holiday_allowance: 25,
    assigned_surveyor_ids: [],
    roles: [], 
    password: '' 
  });
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Staff management state
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);
  
  // Leave management state
  const [leaveSummary, setLeaveSummary] = useState({ pending_requests: 0, approved_this_month: 0, total_days_booked_this_month: 0, employees_on_leave_today: 0 });
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveLoading, setLeaveLoading] = useState(true);
  const [leaveReviewDialogOpen, setLeaveReviewDialogOpen] = useState(false);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [leaveReviewData, setLeaveReviewData] = useState({ status: '', admin_notes: '' });
  
  // Email template management state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: ''
  });
  
  // Backup state
  const [backupLoading, setBackupLoading] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  
  // Trash state
  const [deletedItems, setDeletedItems] = useState({ projects: [], tenders: [], total_count: 0 });
  const [trashLoading, setTrashLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [permanentDeleteId, setPermanentDeleteId] = useState(null);
  
  // Counter management state
  const [counters, setCounters] = useState([]);
  const [countersLoading, setCountersLoading] = useState(false);
  const [editingCounter, setEditingCounter] = useState(null);
  const [counterValue, setCounterValue] = useState('');
  
  const { api, isAdmin, user: currentUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Access denied');
      navigate('/projects');
      return;
    }
    
    let isMounted = true;
    
    const loadInitialData = async () => {
      try {
        const [settingsRes, oneDriveRes, usersRes, staffRes] = await Promise.all([
          api.get('/invoice-settings').catch(() => ({ data: null })),
          api.get('/onedrive/status').catch(() => ({ data: { connected: false } })),
          api.get('/users').catch(() => ({ data: [] })),
          api.get('/staff').catch(() => ({ data: [] }))
        ]);
        
        if (!isMounted) return;
        
        // Set webhook URL from settings
        if (settingsRes.data?.webhook_url) {
          setWebhookUrl(settingsRes.data.webhook_url);
        }
        // Set OneDrive status properly
        setOneDriveStatus({ 
          connected: oneDriveRes.data?.connected || false, 
          user: oneDriveRes.data?.user || null,
          loading: false 
        });
        setUsers(usersRes.data);
        setStaff(staffRes.data);
        setUsersLoading(false);
        setStaffLoading(false);
      } catch (error) {
        console.error('Failed to load settings:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadInitialData();
    
    // Check for OneDrive connection callback
    const oneDriveParam = searchParams.get('onedrive');
    if (oneDriveParam === 'connected') {
      toast.success('OneDrive connected successfully!');
      fetchOneDriveStatus();
    } else if (oneDriveParam === 'error') {
      toast.error('Failed to connect OneDrive: ' + (searchParams.get('message') || 'Unknown error'));
    }
    
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, searchParams, api, navigate]);

  // L-3 — Xero (accounting) integration status + connect callback
  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    api.get('/xero/status')
      .then(r => { if (alive) setXeroStatus({ ...r.data, loading: false }); })
      .catch(() => { if (alive) setXeroStatus(s => ({ ...s, loading: false })); });
    if (searchParams.get('xero') === 'connected') toast.success('Xero connected');
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, api, searchParams]);

  // Fetch leave data when tab changes to leave
  useEffect(() => {
    let isMounted = true;
    
    const loadTabData = async () => {
      if (activeTab === 'leave') {
        const [summaryRes, requestsRes] = await Promise.all([
          api.get('/vacation/leave-summary').catch(() => ({ data: [] })),
          api.get('/vacation-requests').catch(() => ({ data: [] }))
        ]);
        if (isMounted) {
          setLeaveSummary(summaryRes.data);
          setLeaveRequests(requestsRes.data);
        }
      }
      if (activeTab === 'templates') {
        const templatesRes = await api.get('/email-templates').catch(() => ({ data: [] }));
        if (isMounted) setEmailTemplates(templatesRes.data);
      }
      if (activeTab === 'recycle') {
        setTrashLoading(true);
        const deletedRes = await api.get('/admin/deleted-items').catch(() => ({ data: [] }));
        if (isMounted) {
          setDeletedItems(deletedRes.data);
          setTrashLoading(false);
        }
      }
      if (activeTab === 'counters') {
        setCountersLoading(true);
        const countersRes = await api.get('/admin/counters').catch(() => ({ data: [] }));
        if (isMounted) {
          setCounters(countersRes.data);
          setCountersLoading(false);
        }
      }
    };
    
    loadTabData();
    return () => { isMounted = false; };
  }, [activeTab, api]);

  const fetchDeletedItems = async () => {
    setTrashLoading(true);
    try {
      const response = await api.get('/admin/deleted-items');
      setDeletedItems(response.data);
    } catch (error) {
      console.error('Failed to fetch deleted items:', error);
      toast.error('Failed to load deleted items');
    } finally {
      setTrashLoading(false);
    }
  };

  const handleRestoreItem = async (itemType, itemId) => {
    setRestoringId(itemId);
    try {
      const response = await api.post(`/admin/restore/${itemType}/${itemId}`);
      toast.success(response.data.message);
      fetchDeletedItems();
    } catch (error) {
      console.error('Failed to restore item:', error);
      toast.error('Failed to restore item');
    } finally {
      setRestoringId(null);
    }
  };

  const handlePermanentDelete = async (itemType, itemId) => {
    setPermanentDeleteId(itemId);
    try {
      const response = await api.delete(`/admin/permanent-delete/${itemType}/${itemId}`);
      toast.success(response.data.message);
      fetchDeletedItems();
    } catch (error) {
      console.error('Failed to permanently delete item:', error);
      toast.error('Failed to permanently delete item');
    } finally {
      setPermanentDeleteId(null);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setWebhookUrl(response.data.webhook_url || '');
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await api.get('/staff');
      setStaff(response.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    } finally {
      setStaffLoading(false);
    }
  };

  const fetchLeaveSummary = async () => {
    try {
      const response = await api.get('/leave-management/summary');
      setLeaveSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch leave summary:', error);
    }
  };

  const fetchLeaveRequests = async () => {
    setLeaveLoading(true);
    try {
      const response = await api.get('/vacation-requests');
      setLeaveRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch leave requests:', error);
    } finally {
      setLeaveLoading(false);
    }
  };

  const fetchEmailTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const response = await api.get('/email-templates');
      setEmailTemplates(response.data);
      // Seed defaults if no templates exist
      if (response.data.length === 0) {
        await api.post('/email-templates/seed-defaults');
        const refreshed = await api.get('/email-templates');
        setEmailTemplates(refreshed.data);
      }
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.subject.trim() || !templateForm.body.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      if (selectedTemplate) {
        await api.put(`/email-templates/${selectedTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/email-templates', { ...templateForm, template_type: 'supplier_enquiry' });
        toast.success('Template created');
      }
      setTemplateDialogOpen(false);
      setSelectedTemplate(null);
      setTemplateForm({ name: '', subject: '', body: '' });
      fetchEmailTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await api.delete(`/email-templates/${templateId}`);
      toast.success('Template deleted');
      fetchEmailTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const openEditTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body: template.body
    });
    setTemplateDialogOpen(true);
  };

  const fetchOneDriveStatus = async () => {
    try {
      const response = await api.get('/onedrive/status');
      setOneDriveStatus({ ...response.data, loading: false });
    } catch (error) {
      console.error('Failed to fetch OneDrive status:', error);
      setOneDriveStatus({ connected: false, loading: false, error: 'Failed to check status' });
    }
  };

  const handleXeroConnect = async () => {
    try {
      const r = await api.get('/xero/oauth/start');
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Xero is not enabled yet');
    }
  };

  const handleXeroDisconnect = async () => {
    try {
      await api.post('/xero/disconnect');
      toast.success('Xero disconnected');
      setXeroStatus(s => ({ ...s, connected: false, tenant_name: null }));
    } catch {
      toast.error('Failed to disconnect Xero');
    }
  };

  const handleConnectOneDrive = async () => {
    try {
      // Pass window.location.origin so the OAuth redirect lands back on the
      // same domain the user is currently on (luxdev.app, preview, etc.).
      // The backend uses this to build the exact redirect_uri sent to
      // Microsoft, avoiding any env-var drift across environments.
      const response = await api.get('/onedrive/auth-url', {
        params: { origin: window.location.origin }
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Failed to get auth URL:', error);
      toast.error('Failed to initiate OneDrive connection');
    }
  };

  const handleDisconnectOneDrive = async () => {
    if (!confirm('Are you sure you want to disconnect OneDrive?')) return;
    
    try {
      await api.post('/onedrive/disconnect');
      toast.success('OneDrive disconnected');
      setOneDriveStatus({ connected: false, loading: false });
    } catch (error) {
      toast.error('Failed to disconnect OneDrive');
    }
  };

  const handleSetupFolders = async () => {
    setSettingUpFolders(true);
    try {
      const response = await api.post('/onedrive/setup-folders');
      toast.success('Folder structure created successfully!');
      console.log('Folder setup results:', response.data.results);
    } catch (error) {
      console.error('Failed to setup folders:', error);
      toast.error(error.response?.data?.detail || 'Failed to create folder structure');
    } finally {
      setSettingUpFolders(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSaving(true);
    try {
      await api.put('/settings', { webhook_url: webhookUrl || null });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // User Management Functions
  const openEditDialog = (user) => {
    setSelectedUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      user_type: user.user_type || 'employee',
      trade: user.trade || '',
      pay_rate: user.pay_rate || '',
      is_app_user: user.is_app_user || false,
      annual_holiday_allowance: user.annual_holiday_allowance || 25,
      assigned_surveyor_ids: user.assigned_surveyor_ids || [],
      team_contractor_ids: staff.filter(s => (s.assigned_surveyor_ids || []).includes(user.id)).map(s => s.id),
      roles: user.roles || [user.role],
      password: ''
    });
    setEditDialogOpen(true);
  };

  const openCreateDialog = () => {
    setUserForm({ 
      name: '', 
      email: PLACEHOLDER_EMAIL, 
      user_type: 'employee',
      trade: '',
      pay_rate: '',
      is_app_user: false,
      annual_holiday_allowance: 25,
      assigned_surveyor_ids: [],
      team_contractor_ids: [],
      roles: ['operative'], 
      password: '' 
    });
    setCreateDialogOpen(true);
  };

  const isManagerType = (t) => t === 'surveyor' || t === 'admin';
  const saveTeam = async (userId) => {
    if (!isManagerType(userForm.user_type)) return;
    await api.put(`/users/${userId}/team`, { contractor_ids: userForm.team_contractor_ids || [] });
  };

  const openResetPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setResetPasswordDialogOpen(true);
  };

  const handleDeleteStaff = async (userId, userName) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(`${userName} has been deleted`);
      fetchUsers();
      fetchStaff();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleUpdateUser = async () => {
    if (!userForm.name.trim() || !userForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/users/${selectedUser.id}`, {
        name: userForm.name,
        email: userForm.email,
        user_type: userForm.user_type,
        trade: userForm.trade || null,
        pay_rate: userForm.pay_rate ? parseFloat(userForm.pay_rate) : null,
        is_app_user: userForm.is_app_user,
        annual_holiday_allowance: parseInt(userForm.annual_holiday_allowance) || 25,
        assigned_surveyor_ids: userForm.assigned_surveyor_ids || []
      });
      await saveTeam(selectedUser.id);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchUsers();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.name.trim() || !userForm.email.trim() || !userForm.password) {
      toast.error('Name, email and password are required');
      return;
    }
    if (userForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    try {
      const created = await api.post('/users', {
        name: userForm.name,
        email: userForm.email,
        password: userForm.password,
        user_type: userForm.user_type,
        trade: userForm.trade || null,
        pay_rate: userForm.pay_rate ? parseFloat(userForm.pay_rate) : null,
        is_app_user: userForm.is_app_user,
        annual_holiday_allowance: parseInt(userForm.annual_holiday_allowance) || 25,
        assigned_surveyor_ids: userForm.assigned_surveyor_ids || []
      });
      if ((userForm.team_contractor_ids || []).length && created.data?.id) await saveTeam(created.data.id);
      toast.success('User created successfully');
      setCreateDialogOpen(false);
      fetchUsers();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddHoliday = async (userId, days) => {
    try {
      await api.post(`/staff/${userId}/holiday?days=${days}`);
      toast.success('Holiday days added');
      fetchStaff();
      fetchUsers();
    } catch (error) {
      toast.error('Failed to add holiday');
    }
  };

  const handleAddSickDay = async (userId) => {
    try {
      await api.post(`/staff/${userId}/sick?days=1`);
      toast.success('Sick day recorded');
      fetchStaff();
      fetchUsers();
    } catch (error) {
      toast.error('Failed to record sick day');
    }
  };

  const handleResetHolidays = async (userId) => {
    try {
      await api.post(`/staff/${userId}/reset-holidays`);
      toast.success('Holiday counter reset');
      fetchStaff();
      fetchUsers();
    } catch (error) {
      toast.error('Failed to reset holidays');
    }
  };

  // Leave Management Functions
  const openLeaveReviewDialog = (request) => {
    setSelectedLeaveRequest(request);
    setLeaveReviewData({ status: request.status, admin_notes: request.admin_notes || '' });
    setLeaveReviewDialogOpen(true);
  };

  const handleReviewLeave = async () => {
    if (!leaveReviewData.status) {
      toast.error('Please select a status');
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/vacation-requests/${selectedLeaveRequest.id}`, leaveReviewData);
      toast.success(leaveReviewData.status === 'approved' ? 'Leave approved - holiday balance updated' : 'Leave request updated');
      setLeaveReviewDialogOpen(false);
      fetchLeaveRequests();
      fetchLeaveSummary();
      fetchStaff(); // Refresh staff to show updated holiday balance
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLeaveRequest = async (requestId) => {
    if (!confirm('Delete this leave request?')) return;
    try {
      await api.delete(`/vacation-requests/${requestId}`);
      toast.success('Request deleted');
      fetchLeaveRequests();
      fetchLeaveSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete request');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setResetPasswordDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const toggleRole = (role) => {
    setUserForm(prev => {
      const roles = prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role];
      return { ...prev, roles };
    });
  };

  // Counter management functions
  const fetchCounters = async () => {
    setCountersLoading(true);
    try {
      const response = await api.get('/admin/counters');
      setCounters(response.data);
    } catch (error) {
      console.error('Failed to fetch counters:', error);
      toast.error('Failed to load counters');
    } finally {
      setCountersLoading(false);
    }
  };

  const handleUpdateCounter = async (counterId) => {
    const newValue = parseInt(counterValue);
    if (isNaN(newValue) || newValue < 0) {
      toast.error('Please enter a valid positive number');
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/admin/counters/${counterId}?new_value=${newValue}`);
      toast.success('Counter updated successfully');
      setEditingCounter(null);
      setCounterValue('');
      fetchCounters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update counter');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 md:py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <h1 
          className="text-3xl md:text-4xl font-bold text-white"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          Settings
        </h1>
        <p className="text-gray-400 mt-1">
          Configure system integrations and manage users
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-4">
        <Button
          variant={activeTab === 'integrations' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('integrations')}
          className={activeTab === 'integrations' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
        >
          <Cloud className="w-4 h-4 mr-2" />
          Integrations
        </Button>
        <Button
          variant={activeTab === 'users' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('users')}
          className={activeTab === 'users' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
        >
          <UserCog className="w-4 h-4 mr-2" />
          Admin Users
        </Button>
        <Button
          variant={activeTab === 'staff' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('staff')}
          className={activeTab === 'staff' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
        >
          <Users className="w-4 h-4 mr-2" />
          Staff Management
        </Button>
        <Button
          variant={activeTab === 'leave' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('leave')}
          className={activeTab === 'leave' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
        >
          <Palmtree className="w-4 h-4 mr-2" />
          Leave Management
          {leaveSummary.pending_requests > 0 && (
            <span className="ml-2 bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">
              {leaveSummary.pending_requests}
            </span>
          )}
        </Button>
        <Button
          variant={activeTab === 'templates' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('templates')}
          className={activeTab === 'templates' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
        >
          <Mail className="w-4 h-4 mr-2" />
          Email Templates
        </Button>
        <Button
          variant={activeTab === 'backup' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('backup')}
          className={activeTab === 'backup' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
        >
          <Database className="w-4 h-4 mr-2" />
          Backup
        </Button>
        <Button
          variant={activeTab === 'recycle' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('recycle')}
          className={activeTab === 'recycle' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
        >
          <Trash className="w-4 h-4 mr-2" />
          Recycle
          {deletedItems.total_count > 0 && (
            <Badge className="ml-2 bg-red-500/20 text-red-400 text-xs">{deletedItems.total_count}</Badge>
          )}
        </Button>
        <Button
          variant={activeTab === 'counters' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('counters')}
          className={activeTab === 'counters' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
        >
          <Hash className="w-4 h-4 mr-2" />
          Counters
        </Button>
        <Button
          variant={activeTab === 'capture' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('capture')}
          className={activeTab === 'capture' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
          data-testid="capture-settings-tab"
        >
          <Camera className="w-4 h-4 mr-2" />
          Capture
        </Button>
        <Button
          variant={activeTab === 'entities' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('entities')}
          className={activeTab === 'entities' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
          data-testid="entities-settings-tab"
        >
          <Building2 className="w-4 h-4 mr-2" />
          Entities
        </Button>
        <Button
          variant={activeTab === 'inbox-memory' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('inbox-memory')}
          className={activeTab === 'inbox-memory' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
          data-testid="inbox-memory-settings-tab"
        >
          <Brain className="w-4 h-4 mr-2" />
          Inbox memory
        </Button>
        <Button
          variant={activeTab === 'features' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('features')}
          className={activeTab === 'features' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
          data-testid="features-settings-tab"
        >
          <ToggleLeft className="w-4 h-4 mr-2" />
          Features
        </Button>
        <Button
          variant={activeTab === 'order-suppliers' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('order-suppliers')}
          className={activeTab === 'order-suppliers' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
          data-testid="order-suppliers-settings-tab"
        >
          <Store className="w-4 h-4 mr-2" />
          Order suppliers
        </Button>
        <Button
          variant={activeTab === 'building-control' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('building-control')}
          className={activeTab === 'building-control' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
          data-testid="building-control-settings-tab"
        >
          <HardHat className="w-4 h-4 mr-2" />
          Building Control
        </Button>
        <Button
          variant={activeTab === 'projects' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('projects')}
          className={activeTab === 'projects' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
          data-testid="projects-settings-tab"
        >
          <GitMerge className="w-4 h-4 mr-2" />
          Projects
        </Button>
        <Button
          variant={activeTab === 'impersonation' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('impersonation')}
          className={activeTab === 'impersonation' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'}
          data-testid="impersonation-log-tab"
        >
          <Eye className="w-4 h-4 mr-2" />
          Impersonation log
        </Button>
      </div>

      {activeTab === 'inbox-memory' && <InboxMemoryTab api={api} />}
      {activeTab === 'features' && <FeaturesTab api={api} onChanged={() => refreshUser?.()} />}
      {activeTab === 'order-suppliers' && <OrderSuppliersTab api={api} />}
      {activeTab === 'building-control' && <BuildingControlTab api={api} />}
      {activeTab === 'projects' && <MergeProjectsTab api={api} />}
      {activeTab === 'impersonation' && <ImpersonationLogTab api={api} />}

      {/* Leave Management Tab */}
      {activeTab === 'leave' && (
        <LeaveManagementTab
          leaveSummary={leaveSummary}
          leaveRequests={leaveRequests}
          leaveLoading={leaveLoading}
          staff={staff}
          api={api}
          fetchLeaveRequests={fetchLeaveRequests}
          fetchLeaveSummary={fetchLeaveSummary}
          fetchStaff={fetchStaff}
          openLeaveReviewDialog={openLeaveReviewDialog}
          handleDeleteLeaveRequest={handleDeleteLeaveRequest}
        />
      )}

      {/* Staff Management Tab */}
      {activeTab === 'staff' && (
        <Card className="bg-[#2A3240] border-white/10 mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-md">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-xl text-white">Staff Management</CardTitle>
                  <CardDescription className="text-gray-400">Manage employees and sub-contractors</CardDescription>
                </div>
              </div>
              <Button
                onClick={openCreateDialog}
                data-testid="add-staff-btn"
                className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Staff
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {staffLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" />
              </div>
            ) : staff.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No staff members yet</p>
            ) : (
              <div className="space-y-3">
                {staff.map(member => (
                  <div 
                    key={member.id} 
                    className="flex flex-col lg:flex-row lg:items-center justify-between p-4 bg-[#1E2530] rounded-lg gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-medium">{member.name}</span>
                        <Badge className={USER_TYPE_COLORS[member.user_type] || 'bg-gray-500/20'}>
                          {member.user_type === 'sub_contractor' ? 'Sub-contractor' : 'Employee'}
                        </Badge>
                        {member.is_app_user && (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            App User
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-400 space-y-1">
                        <p>{member.email}</p>
                        {member.trade && (
                          <p>Trade: <span className="text-white">{member.trade}</span></p>
                        )}
                        {member.pay_rate && (
                          <p>Pay Rate: <span className="text-white">£{member.pay_rate}/day</span></p>
                        )}
                      </div>
                    </div>
                    
                    {/* Holiday Tracking (Employees only) */}
                    {member.user_type === 'employee' && (
                      <div className="flex flex-col gap-2 bg-[#2A3240] p-3 rounded-lg min-w-[200px]">
                        <div className="text-sm text-gray-400">
                          <div className="flex justify-between">
                            <span>Holiday Allowance:</span>
                            <span className="text-white">{member.annual_holiday_allowance} days</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Days Taken:</span>
                            <span className="text-amber-400">{member.holidays_taken} days</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Remaining:</span>
                            <span className="text-green-400">{member.holidays_remaining} days</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Sick Days:</span>
                            <span className="text-red-400">{member.sick_days} days</span>
                          </div>
                        </div>
                        <div className="flex gap-1 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAddHoliday(member.id, 1)}
                            className="text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
                          >
                            +1 Holiday
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAddSickDay(member.id)}
                            className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            +1 Sick
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      {/* Capture Access Toggle */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            const newVal = !member.capture_enabled;
                            await api.put(`/users/${member.id}/capture-enabled`, { enabled: newVal });
                            setStaff(prev => prev.map(s => s.id === member.id ? { ...s, capture_enabled: newVal } : s));
                            toast.success(`Capture ${newVal ? 'enabled' : 'disabled'} for ${member.name}`);
                          } catch { toast.error('Failed to toggle capture access'); }
                        }}
                        className={member.capture_enabled
                          ? 'border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10'
                          : 'border-white/10 text-gray-500 hover:bg-white/5'}
                        title={member.capture_enabled ? 'Capture enabled — click to disable' : 'Capture disabled — click to enable'}
                        data-testid={`capture-toggle-${member.id}`}
                      >
                        <Camera className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(member)}
                        className="border-white/20 text-gray-300 hover:bg-white/5"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openResetPasswordDialog(member)}
                        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                      >
                        <Key className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete "${member.name}"? This action cannot be undone.`)) {
                            handleDeleteStaff(member.id, member.name);
                          }
                        }}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Email Templates Tab */}
      {activeTab === 'templates' && (
        <Card className="bg-[#2A3240] border-white/10 mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-md">
                  <Mail className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-xl text-white">Email Templates</CardTitle>
                  <CardDescription className="text-gray-400">Manage supplier enquiry email templates</CardDescription>
                </div>
              </div>
              <Button
                onClick={() => {
                  setSelectedTemplate(null);
                  setTemplateForm({ name: '', subject: '', body: '' });
                  setTemplateDialogOpen(true);
                }}
                data-testid="add-template-btn"
                className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Template
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {templatesLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" />
              </div>
            ) : emailTemplates.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-400 mb-4">No email templates yet</p>
                <Button
                  onClick={async () => {
                    try {
                      await api.post('/email-templates/seed-defaults');
                      fetchEmailTemplates();
                      toast.success('Default templates created');
                    } catch (error) {
                      toast.error('Failed to create defaults');
                    }
                  }}
                  variant="outline"
                  className="border-white/20 text-gray-300"
                >
                  Create Default Templates
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Template placeholder info */}
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-300 font-medium mb-2">Available placeholders:</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{supplier_name}'}</code>
                    <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{tender_ref}'}</code>
                    <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{address}'}</code>
                    <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{postcode}'}</code>
                    <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{project_types}'}</code>
                    <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{trade}'}</code>
                    <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{description}'}</code>
                    <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{user_name}'}</code>
                  </div>
                </div>
                
                {emailTemplates.map(template => (
                  <div 
                    key={template.id}
                    className="p-4 bg-[#1E2530] rounded-lg border border-white/5"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-medium text-white">{template.name}</h3>
                          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                            {template.template_type}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-400 mb-2">
                          <span className="text-gray-500">Subject:</span> {template.subject}
                        </p>
                        <p className="text-xs text-gray-500 line-clamp-2">{template.body.substring(0, 150)}...</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditTemplate(template)}
                          className="text-gray-400 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="text-gray-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Backup Tab */}
      {activeTab === 'backup' && (
        <BackupTab
          api={api}
          backupLoading={backupLoading}
          setBackupLoading={setBackupLoading}
          lastBackup={lastBackup}
          setLastBackup={setLastBackup}
        />
      )}

      {/* Recycle Tab */}
      {activeTab === 'recycle' && (
        <RecycleBinTab
          trashLoading={trashLoading}
          deletedItems={deletedItems}
          restoringId={restoringId}
          permanentDeleteId={permanentDeleteId}
          fetchDeletedItems={fetchDeletedItems}
          handleRestoreItem={handleRestoreItem}
          handlePermanentDelete={handlePermanentDelete}
        />
      )}

      {/* Counters Tab */}
      {activeTab === 'counters' && (
        <Card className="bg-[#2A3240] border-white/10 mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500/10 rounded-md">
                <Hash className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <CardTitle className="text-xl text-white">Counter Management</CardTitle>
                <CardDescription className="text-gray-400">
                  Manage project and tender numbering counters
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Warning message */}
            <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-300">
                  <p className="font-medium mb-1">Important</p>
                  <p className="text-amber-400/80">
                    Changing counters will affect the next number assigned to new projects/tenders. 
                    Make sure you've deleted any test items before adjusting these values.
                  </p>
                </div>
              </div>
            </div>

            {countersLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" />
              </div>
            ) : counters.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No counters found</p>
            ) : (
              <div className="space-y-4">
                {counters.map(counter => (
                  <div 
                    key={counter.id}
                    className="p-4 bg-[#1E2530] rounded-lg border border-white/5"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-white">{counter.name}</h3>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-gray-400">
                            Current: <span className="text-white font-mono">{counter.current_value}</span>
                          </span>
                          <span className="text-gray-400">
                            Next: <span className="text-cyan-400 font-mono">{counter.next_number}</span>
                          </span>
                        </div>
                      </div>
                      
                      {editingCounter === counter.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            value={counterValue}
                            onChange={(e) => setCounterValue(e.target.value)}
                            placeholder="New value"
                            className="w-32 bg-[#2A3240] border-white/20 text-white"
                            data-testid={`counter-input-${counter.id}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => handleUpdateCounter(counter.id)}
                            disabled={submitting}
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingCounter(null);
                              setCounterValue('');
                            }}
                            className="border-white/20 text-gray-400"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingCounter(counter.id);
                            setCounterValue(counter.current_value.toString());
                          }}
                          className="border-white/20 text-gray-300 hover:bg-white/5"
                          data-testid={`edit-counter-${counter.id}`}
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Help text */}
            <div className="mt-6 p-4 bg-[#1E2530] rounded-lg border border-white/10">
              <h4 className="text-sm font-medium text-white mb-2">How counters work</h4>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• <strong>Current value</strong>: The last number that was used</li>
                <li>• <strong>Next number</strong>: What the next created item will receive</li>
                <li>• To set the next project as LC1079, set the counter to 1078</li>
                <li>• Changes take effect immediately for new items</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Management Tab */}
      {activeTab === 'users' && (
      <Card className="bg-[#2A3240] border-white/10 mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-md">
                <UserCog className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-xl text-white">Admin Users</CardTitle>
                <CardDescription className="text-gray-400">Manage admin and surveyor accounts</CardDescription>
              </div>
            </div>
            <Button
              onClick={openCreateDialog}
              data-testid="add-user-btn"
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {usersLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div 
                  key={user.id}
                  className="flex items-center justify-between p-4 bg-[#1E2530] rounded-lg border border-white/5"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-white truncate">{user.name}</p>
                      {user.id === currentUser?.id && (
                        <Badge className="bg-[#F5A623]/20 text-[#F5A623] border-[#F5A623]/30 text-xs">You</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">{user.email}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(user.roles || [user.role]).map(role => (
                        <Badge key={role} className={`${ROLE_COLORS[role]} text-xs capitalize`}>
                          {role}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const newVal = !user.capture_enabled;
                          await api.put(`/users/${user.id}/capture-enabled`, { enabled: newVal });
                          setUsers(prev => prev.map(u => u.id === user.id ? { ...u, capture_enabled: newVal } : u));
                          toast.success(`Capture ${newVal ? 'enabled' : 'disabled'} for ${user.name}`);
                        } catch { toast.error('Failed to toggle capture access'); }
                      }}
                      className={user.capture_enabled
                        ? 'border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10'
                        : 'border-white/10 text-gray-500 hover:bg-white/5'}
                      title={user.capture_enabled ? 'Capture enabled — click to disable' : 'Capture disabled — click to enable'}
                      data-testid={`admin-capture-toggle-${user.id}`}
                    >
                      <Camera className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openResetPasswordDialog(user)}
                      className="text-gray-400 hover:text-white"
                      title="Reset Password"
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(user)}
                      className="text-gray-400 hover:text-white"
                      title="Edit User"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {user.id !== currentUser?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-gray-400 hover:text-red-400"
                        title="Delete User"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Capture Settings Tab */}
      {activeTab === 'capture' && (
        <CaptureSettingsTab />
      )}

      {activeTab === 'entities' && (
        <EntitiesSettingsTab api={api} />
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
      <>
      {/* Xero accounting integration (L-3) — built now, OFF until connected */}
      <Card className="bg-[#2A3240] border-white/10 mb-6" data-testid="xero-integration-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/10 rounded-md">
              <Landmark className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-xl text-white">Xero (Accounting)</CardTitle>
              <CardDescription className="text-gray-400">
                Push posted supplier invoices to Xero as draft bills. Xero is the accounting source of truth.
              </CardDescription>
            </div>
            {xeroStatus.loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
            ) : xeroStatus.connected ? (
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm">Connected{xeroStatus.tenant_name ? ` · ${xeroStatus.tenant_name}` : ''}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-gray-500">
                <XCircle className="w-5 h-5" />
                <span className="text-sm">{xeroStatus.enabled ? 'Not connected' : 'Disabled'}</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!xeroStatus.enabled ? (
            <p className="text-sm text-gray-400" data-testid="xero-disabled-note">
              Xero isn't enabled yet. Add your Xero app <b>Client ID</b> and <b>Secret</b> (from developer.xero.com)
              to the backend config and set <code className="text-gray-300">XERO_ENABLED=true</code>, then reload to connect your organisation.
            </p>
          ) : xeroStatus.connected ? (
            <Button variant="outline" onClick={handleXeroDisconnect}
              className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="xero-disconnect-btn">
              Disconnect Xero
            </Button>
          ) : (
            <Button onClick={handleXeroConnect} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="xero-connect-btn">
              Connect to Xero
            </Button>
          )}
        </CardContent>
      </Card>

      {/* OneDrive Integration */}
      <Card className="bg-[#2A3240] border-white/10 mb-6">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-md">
              <Cloud className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-xl text-white">
                OneDrive Integration
              </CardTitle>
              <CardDescription className="text-gray-400">
                Connect to OneDrive for document storage
              </CardDescription>
            </div>
            {oneDriveStatus.loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
            ) : oneDriveStatus.connected ? (
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm">Connected</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-gray-500">
                <XCircle className="w-5 h-5" />
                <span className="text-sm">Not connected</span>
              </div>
            )}
          </div>
        </CardHeader>
        
        <CardContent>
          {oneDriveStatus.connected ? (
            <div className="space-y-4">
              {/* Connected User Info */}
              {oneDriveStatus.user && (
                <div className="p-4 bg-[#1E2530] rounded-md border border-green-500/20">
                  <p className="text-sm text-gray-400 mb-1">Connected as:</p>
                  <p className="text-white font-medium">{oneDriveStatus.user.name}</p>
                  <p className="text-sm text-gray-500">{oneDriveStatus.user.email}</p>
                </div>
              )}
              
              {/* Setup Folder Structure */}
              <div className="p-4 bg-[#1E2530] rounded-md border border-white/10">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 text-sm text-gray-400">
                    <p className="mb-2">
                      Verify SharePoint folder structure for automatic folder creation:
                    </p>
                    <pre className="bg-[#151A22] p-3 rounded text-xs overflow-x-auto text-gray-300 mb-3">
{`📁 SharePoint: LUXDevelopments/
├── 📁 01. Tenders/     ← New tender folders created here
└── 📁 02. Live Jobs/   ← New project folders created here`}
                    </pre>
                    <Button
                      onClick={handleSetupFolders}
                      disabled={settingUpFolders}
                      size="sm"
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      {settingUpFolders ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <FolderPlus className="w-4 h-4 mr-2" />
                          Verify Folder Structure
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Disconnect Button */}
              <Button
                variant="outline"
                onClick={handleDisconnectOneDrive}
                className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <Unlink className="w-4 h-4 mr-2" />
                Disconnect OneDrive
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-[#1E2530] rounded-md border border-white/10">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-gray-400">
                    <p className="mb-2">
                      Connect your Microsoft account to enable:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-gray-500">
                      <li>Automatic folder creation for projects & tenders</li>
                      <li>Document storage and management</li>
                      <li>File browsing from within the app</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <Button
                onClick={handleConnectOneDrive}
                data-testid="connect-onedrive-btn"
                className="w-full bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Link2 className="w-4 h-4 mr-2" />
                Connect OneDrive
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SharePoint/Power Automate Integration */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#F5A623]/10 rounded-md">
              <Webhook className="w-5 h-5 text-[#F5A623]" />
            </div>
            <div>
              <CardTitle className="text-xl text-white">
                SharePoint / Power Automate
              </CardTitle>
              <CardDescription className="text-gray-400">
                Configure webhook for automated folder creation
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Info Box */}
          <div className="mb-6 p-4 bg-[#1E2530] rounded-md border border-white/10">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-400">
                <p className="mb-2">
                  When a new project is created, a POST request will be sent to this URL with the following payload:
                </p>
                <pre className="bg-[#151A22] p-3 rounded text-xs overflow-x-auto text-gray-300">
{`{
  "project_ref": "LUX100 - 42 Victoria St, NN1 3AB",
  "address_line1": "42 Victoria Street",
  "town_city": "Northampton",
  "postcode": "NN1 3AB",
  "created_by": "user-id",
  "created_at": "2024-01-15T10:30:00Z"
}`}
                </pre>
                <p className="mt-3">
                  Use this with Power Automate to create folders in SharePoint/OneDrive at:
                </p>
                <code className="text-[#F5A623] text-xs">
                  /LUX Operations/{'{projectRef}'}/
                </code>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="webhook_url" className="text-gray-300">
                Webhook URL
              </Label>
              <Input
                id="webhook_url"
                type="url"
                placeholder="https://prod-XX.westeurope.logic.azure.com/workflows/..."
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                data-testid="webhook-url-input"
                className="bg-[#1E2530] border-white/20 text-white placeholder:text-gray-500 focus:border-[#F5A623]"
              />
              <p className="text-xs text-gray-500">
                Leave empty to disable webhook notifications
              </p>
            </div>

            <Button
              type="submit"
              disabled={saving}
              data-testid="save-settings-btn"
              className="w-full bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </form>

          {/* Help Link */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <a
              href="https://learn.microsoft.com/en-us/power-automate/get-started-logic-flow"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-[#F5A623] hover:underline"
            >
              <ExternalLink className="w-4 h-4" />
              Learn how to create a Power Automate flow
            </a>
          </div>
        </CardContent>
      </Card>
      </>
      )}

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserCog className="w-5 h-5 text-[#F5A623]" />
              Edit User
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Update user details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Name</Label>
                <Input
                  value={userForm.name}
                  onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Full name"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Email</Label>
                <Input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">User Type</Label>
              <Select 
                value={userForm.user_type} 
                onValueChange={(v) => setUserForm(prev => ({ ...prev, user_type: v }))}
              >
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {USER_TYPE_OPTIONS.map(type => (
                    <SelectItem key={type.value} value={type.value} className="text-white focus:bg-[#F5A623]/20">
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {USER_TYPE_OPTIONS.find(t => t.value === userForm.user_type)?.description}
              </p>
            </div>

            {/* Trade & Pay Rate (for employees and sub-contractors) */}
            {(userForm.user_type === 'employee' || userForm.user_type === 'sub_contractor') && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Trade</Label>
                  <Select 
                    value={userForm.trade || ''} 
                    onValueChange={(v) => setUserForm(prev => ({ ...prev, trade: v }))}
                  >
                    <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                      <SelectValue placeholder="Select trade" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2A3240] border-white/10">
                      {TRADE_OPTIONS.map(trade => (
                        <SelectItem key={trade} value={trade} className="text-white focus:bg-[#F5A623]/20">
                          {trade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Pay Rate (£/day)</Label>
                  <Input
                    type="number"
                    value={userForm.pay_rate}
                    onChange={(e) => setUserForm(prev => ({ ...prev, pay_rate: e.target.value }))}
                    placeholder="150"
                    className="bg-[#1E2530] border-white/20 text-white"
                  />
                </div>
              </div>
            )}

            {/* Holiday Allowance (for employees) */}
            {userForm.user_type === 'employee' && (
              <div className="space-y-2">
                <Label className="text-gray-300">Annual Holiday Allowance (days)</Label>
                <Input
                  type="number"
                  value={userForm.annual_holiday_allowance}
                  onChange={(e) => setUserForm(prev => ({ ...prev, annual_holiday_allowance: e.target.value }))}
                  placeholder="25"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
            )}

            {/* App User Permission (for employees and sub-contractors) */}
            {(userForm.user_type === 'employee' || userForm.user_type === 'sub_contractor') && (
              <div className="flex items-center space-x-3 p-3 rounded bg-[#1E2530]">
                <Checkbox
                  id="is_app_user"
                  checked={userForm.is_app_user}
                  onCheckedChange={(checked) => setUserForm(prev => ({ ...prev, is_app_user: checked }))}
                  className="border-white/30 data-[state=checked]:bg-[#F5A623] data-[state=checked]:border-[#F5A623]"
                />
                <div className="flex-1">
                  <Label htmlFor="is_app_user" className="text-white cursor-pointer">
                    App User Access
                  </Label>
                  <p className="text-xs text-gray-500">
                    Can view schedule, project addresses, request holidays, and clock in/out
                  </p>
                </div>
              </div>
            )}

            <StaffTeamPicker
              variant="edit"
              userType={userForm.user_type}
              users={users}
              staff={staff}
              managerIds={userForm.assigned_surveyor_ids || []}
              contractorIds={userForm.team_contractor_ids || []}
              onManagersChange={(ids) => setUserForm(prev => ({ ...prev, assigned_surveyor_ids: ids }))}
              onContractorsChange={(ids) => setUserForm(prev => ({ ...prev, team_contractor_ids: ids }))}
            />

            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setEditDialogOpen(false)} 
                className="flex-1 border-white/20 text-gray-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateUser} 
                disabled={submitting}
                className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#F5A623]" />
              Add New User
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Create a new user account
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Name *</Label>
                <Input
                  value={userForm.name}
                  onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Full name"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Email *</Label>
                <Input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Password *</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Minimum 6 characters"
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">User Type *</Label>
              <Select 
                value={userForm.user_type} 
                onValueChange={(v) => setUserForm(prev => ({ ...prev, user_type: v }))}
              >
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {USER_TYPE_OPTIONS.map(type => (
                    <SelectItem key={type.value} value={type.value} className="text-white focus:bg-[#F5A623]/20">
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {USER_TYPE_OPTIONS.find(t => t.value === userForm.user_type)?.description}
              </p>
            </div>

            {/* Trade & Pay Rate (for employees and sub-contractors) */}
            {(userForm.user_type === 'employee' || userForm.user_type === 'sub_contractor') && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Trade</Label>
                  <Select 
                    value={userForm.trade || ''} 
                    onValueChange={(v) => setUserForm(prev => ({ ...prev, trade: v }))}
                  >
                    <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                      <SelectValue placeholder="Select trade" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2A3240] border-white/10">
                      {TRADE_OPTIONS.map(trade => (
                        <SelectItem key={trade} value={trade} className="text-white focus:bg-[#F5A623]/20">
                          {trade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Pay Rate (£/day)</Label>
                  <Input
                    type="number"
                    value={userForm.pay_rate}
                    onChange={(e) => setUserForm(prev => ({ ...prev, pay_rate: e.target.value }))}
                    placeholder="150"
                    className="bg-[#1E2530] border-white/20 text-white"
                  />
                </div>
              </div>
            )}

            {/* Holiday Allowance (for employees) */}
            {userForm.user_type === 'employee' && (
              <div className="space-y-2">
                <Label className="text-gray-300">Annual Holiday Allowance (days)</Label>
                <Input
                  type="number"
                  value={userForm.annual_holiday_allowance}
                  onChange={(e) => setUserForm(prev => ({ ...prev, annual_holiday_allowance: e.target.value }))}
                  placeholder="25"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
            )}

            {/* App User Permission (for employees and sub-contractors) */}
            {(userForm.user_type === 'employee' || userForm.user_type === 'sub_contractor') && (
              <div className="flex items-center space-x-3 p-3 rounded bg-[#1E2530]">
                <Checkbox
                  id="create_is_app_user"
                  checked={userForm.is_app_user}
                  onCheckedChange={(checked) => setUserForm(prev => ({ ...prev, is_app_user: checked }))}
                  className="border-white/30 data-[state=checked]:bg-[#F5A623] data-[state=checked]:border-[#F5A623]"
                />
                <div className="flex-1">
                  <Label htmlFor="create_is_app_user" className="text-white cursor-pointer">
                    App User Access
                  </Label>
                  <p className="text-xs text-gray-500">
                    Can view schedule, project addresses, request holidays, and clock in/out
                  </p>
                </div>
              </div>
            )}

            <StaffTeamPicker
              variant="create"
              userType={userForm.user_type}
              users={users}
              staff={staff}
              managerIds={userForm.assigned_surveyor_ids || []}
              contractorIds={userForm.team_contractor_ids || []}
              onManagersChange={(ids) => setUserForm(prev => ({ ...prev, assigned_surveyor_ids: ids }))}
              onContractorsChange={(ids) => setUserForm(prev => ({ ...prev, team_contractor_ids: ids }))}
            />

            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setCreateDialogOpen(false)} 
                className="flex-1 border-white/20 text-gray-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateUser} 
                disabled={submitting}
                className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create User'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-[#F5A623]" />
              Reset Password
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Set a new password for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-gray-300">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setResetPasswordDialogOpen(false)} 
                className="flex-1 border-white/20 text-gray-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleResetPassword} 
                disabled={submitting}
                className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leave Review Dialog */}
      <Dialog open={leaveReviewDialogOpen} onOpenChange={setLeaveReviewDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Palmtree className="w-5 h-5 text-green-400" />
              Review Leave Request
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedLeaveRequest && (
                <>
                  {selectedLeaveRequest.employee_name} - {' '}
                  {format(parseISO(selectedLeaveRequest.start_date), 'dd MMM')} to {' '}
                  {format(parseISO(selectedLeaveRequest.end_date), 'dd MMM yyyy')}
                  {' '}({differenceInDays(parseISO(selectedLeaveRequest.end_date), parseISO(selectedLeaveRequest.start_date)) + 1} days)
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Status</Label>
              <Select 
                value={leaveReviewData.status} 
                onValueChange={(v) => setLeaveReviewData(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  <SelectItem value="pending" className="text-white focus:bg-[#F5A623]/20">
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-400" />
                      Pending
                    </span>
                  </SelectItem>
                  <SelectItem value="approved" className="text-white focus:bg-[#F5A623]/20">
                    <span className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      Approved
                    </span>
                  </SelectItem>
                  <SelectItem value="denied" className="text-white focus:bg-[#F5A623]/20">
                    <span className="flex items-center gap-2">
                      <X className="w-4 h-4 text-red-400" />
                      Denied
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {leaveReviewData.status === 'approved' && (
                <p className="text-xs text-green-400">
                  Approving will automatically deduct days from the employee's holiday balance.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Admin Notes (Optional)</Label>
              <Input
                value={leaveReviewData.admin_notes}
                onChange={(e) => setLeaveReviewData(prev => ({ ...prev, admin_notes: e.target.value }))}
                placeholder="Add a note for the employee..."
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setLeaveReviewDialogOpen(false)} 
                className="flex-1 border-white/20 text-gray-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleReviewLeave} 
                disabled={submitting}
                className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              {selectedTemplate ? 'Edit Email Template' : 'Create Email Template'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Create email templates for supplier enquiries. Use placeholders for dynamic content.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Template Name *</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Labour Only, Supply & Fit"
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Email Subject *</Label>
              <Input
                value={templateForm.subject}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="e.g., Request for Quote - {tender_ref} - {trade} Works"
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Email Body *</Label>
              <textarea
                value={templateForm.body}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Enter the email body text..."
                rows={12}
                className="w-full bg-[#1E2530] border border-white/20 text-white rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#F5A623]"
              />
            </div>
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-300 font-medium mb-2">Available placeholders:</p>
              <div className="flex flex-wrap gap-2 text-xs">
                <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{supplier_name}'}</code>
                <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{tender_ref}'}</code>
                <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{address}'}</code>
                <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{postcode}'}</code>
                <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{project_types}'}</code>
                <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{trade}'}</code>
                <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{description}'}</code>
                <code className="bg-[#1E2530] px-2 py-1 rounded text-gray-300">{'{user_name}'}</code>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => { 
                  setTemplateDialogOpen(false); 
                  setSelectedTemplate(null);
                  setTemplateForm({ name: '', subject: '', body: '' });
                }} 
                className="flex-1 border-white/20 text-gray-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveTemplate} 
                disabled={submitting || !templateForm.name.trim() || !templateForm.subject.trim() || !templateForm.body.trim()}
                className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Template'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
